// @ts-strict-ignore
import { communicationService } from '@ad-engine/communication';
import { Injectable } from '@wikia/dependency-injection';
import { AdvertisementLabel } from '../../ad-products';
import { AdSlotEvent, RepeatConfig } from '../models';
import {
	getTopOffset,
	getViewportHeight,
	isInTheSameViewport,
	isInViewport,
	isOneOfExperimentsActiveFromContext,
	logger,
	stringBuilder,
} from '../utils';
import { context } from './context-service';
import { SlotRepeater } from './slot-repeater';
import { utils } from '../index';
export type InsertMethodType = 'alter' | 'append' | 'prepend' | 'after' | 'before';

export interface SlotCreatorConfig {
	slotName: string;
	insertMethod: InsertMethodType;
	/* The anchor selector can contain a string template, that references the slot config's repeat.index value if it exists.
	 * The indexBasedAnchorSelector property needs to be set to true, in order for the string templating to work.
	 * Example - incontent-boxad-{slotConfig.repeat.index}
	 *
	 * The above example could be a wrapper element that was created for a repeatable incontent_boxad unit.
	 */
	anchorSelector: string;
	anchorElement?: HTMLElement;
	/**
	 * @default firstViable
	 */
	anchorPosition?: number | 'firstViable' | 'belowFirstViewport' | 'belowScrollPosition';
	avoidConflictWith?: string[];
	classList?: string[];
	repeat?: RepeatConfig;
	placeholderConfig?: SlotPlaceholderContextConfig;
}

export interface SlotCreatorWrapperConfig {
	id?: string;
	classList?: string[];
}

export interface SlotPlaceholderContextConfig {
	createLabel?: boolean;
	adLabelParent?: string;
}

const groupName = 'slot-creator';

@Injectable()
export class SlotCreator {
	constructor(private slotRepeater = new SlotRepeater()) {}

	createSlot(
		slotLooseConfig: SlotCreatorConfig,
		wrapperLooseConfig?: SlotCreatorWrapperConfig,
	): HTMLElement {
		if (!slotLooseConfig) {
			return null;
		}

		logger(groupName, `Creating: ${slotLooseConfig.slotName}`, slotLooseConfig, wrapperLooseConfig);

		const slotConfig = this.fillSlotConfig(slotLooseConfig);
		const slot = this.makeSlot(slotConfig, slotLooseConfig.anchorElement);

		if (slotConfig.repeat) {
			this.setupSlotRepeat(slotConfig);
		}

		if (slotLooseConfig.insertMethod === 'alter') {
			return slot;
		}

		const wrapper = this.wrapSlot(slot, wrapperLooseConfig);
		const anchorElement = this.getAnchorElement(slotConfig);

		if (this.isCollapsedAdInPlaceholder(anchorElement)) {
			return;
		}

		anchorElement[slotConfig.insertMethod](wrapper);

		if (slotConfig.placeholderConfig?.createLabel) {
			this.addAdLabel(slot.parentElement, slotConfig.slotName);
		}

		return slot;
	}

	private fillSlotConfig(slotLooseConfig: SlotCreatorConfig): Required<SlotCreatorConfig> {
		return {
			...slotLooseConfig,
			anchorElement: slotLooseConfig.anchorElement || null,
			anchorPosition: slotLooseConfig.anchorPosition ?? 'firstViable',
			avoidConflictWith: slotLooseConfig.avoidConflictWith || [],
			classList: slotLooseConfig.classList || [],
			repeat: slotLooseConfig.repeat,
			placeholderConfig: slotLooseConfig.placeholderConfig,
		};
	}

	private isCollapsedAdInPlaceholder(anchorElement) {
		return anchorElement.querySelector('.hidead')?.getAttribute('data-slot-result') === 'collapse';
	}

	private getAnchorElement(slotConfig: Required<SlotCreatorConfig>): HTMLElement {
		const anchorElements = this.getAnchorElements(slotConfig);
		const conflictElements = this.getConflictElements(slotConfig);

		if (
			slotConfig.slotName === 'incontent_boxad_1' &&
			// We don't want this check on for the Desktop ICB units
			!isOneOfExperimentsActiveFromContext(['stickyRightRailWithICB'])
		) {
			const slotInViewport = [...anchorElements].find((anchorElement) =>
				isInViewport(anchorElement),
			);

			if (slotInViewport) {
				logger(groupName, 'getAnchorElement() called', slotConfig, slotInViewport);

				return slotInViewport;
			}
		}

		const result = anchorElements.find(
			(anchorElement) => !isInTheSameViewport(anchorElement, conflictElements),
		);

		if (!result) {
			this.throwNoPlaceToInsertError(slotConfig.slotName);
		}

		logger(groupName, 'getAnchorElement() called', slotConfig, result);

		return result;
	}

	/**
	 * Adjusts the anchor selector for repeatable slots by injecting the repeat index into the selector string template.
	 * This is only activated when the repeatIndexBasedAnchorSelector property is set to true in the slot configuration.
	 * Additional checks were added so this only runs for the Sticky Right Rail with multiple ICBs experiment.
	 *
	 * @param {Required<SlotCreatorConfig>} slotConfig - The configuration object for the slot creator.
	 * @returns {string} - The adjusted anchor selector string.
	 */
	private adjustAnchorSelectorForRepeat(slotConfig: Required<SlotCreatorConfig>): string {
		let anchorSelector = slotConfig.anchorSelector;
		if (
			slotConfig.repeat?.indexBasedAnchorSelector &&
			utils.isOneOfExperimentsActiveFromContext(['stickyRightRailWithICB'])
		) {
			// Inject the repeatIndex into the anchor selector through string templates
			anchorSelector = stringBuilder.build(anchorSelector, {
				slotConfig: {
					...slotConfig,
				},
			});
		}
		return anchorSelector;
	}

	private getAnchorElements(slotConfig: Required<SlotCreatorConfig>): HTMLElement[] {
		const elements: HTMLElement[] = Array.from(
			document.querySelectorAll(this.adjustAnchorSelectorForRepeat(slotConfig)),
		);

		switch (slotConfig.anchorPosition) {
			case 'belowFirstViewport': {
				return elements.filter((el) => getTopOffset(el) > getViewportHeight());
			}
			case 'belowScrollPosition': {
				return elements.filter((el) => getTopOffset(el) > window.scrollY);
			}
			case 'firstViable': {
				return elements;
			}
			default: {
				const element = elements[slotConfig.anchorPosition];

				if (!element) {
					this.throwNoPlaceToInsertError(slotConfig.slotName);
				}

				return [element];
			}
		}
	}

	private getConflictElements(slotConfig: Required<SlotCreatorConfig>): HTMLElement[] {
		const elements: HTMLElement[] = [];

		slotConfig.avoidConflictWith.forEach((selector) => {
			const selected: HTMLElement[] = Array.from(document.querySelectorAll(selector));

			elements.push(...selected);
		});

		return elements;
	}

	private makeSlot(
		slotConfig: Required<SlotCreatorConfig>,
		domSlotElement: HTMLElement = null,
	): HTMLElement {
		const slot = domSlotElement || document.createElement('div');

		if (!slot) {
			return;
		}

		slot.id = slotConfig.slotName;
		slot.classList.add('gpt-ad', ...slotConfig.classList);

		return slot;
	}

	private wrapSlot(slot: HTMLElement, wrapperLooseConfig?: SlotCreatorWrapperConfig): HTMLElement {
		if (!wrapperLooseConfig) {
			return slot;
		}

		const wrapper = document.createElement('div');

		if (wrapperLooseConfig.classList) {
			wrapper.classList.add(...wrapperLooseConfig.classList);
		}
		if (wrapperLooseConfig.id) {
			wrapper.id = wrapperLooseConfig.id;
		}
		wrapper.append(slot);

		return wrapper;
	}

	private addAdLabel(placeholder: HTMLElement, slotName: string): void {
		const advertisementLabel = new AdvertisementLabel({
			slot: slotName,
		});
		placeholder.appendChild(advertisementLabel.render());
	}

	private setupSlotRepeat(slotConfig: Required<SlotCreatorConfig>): void {
		communicationService.onSlotEvent(
			AdSlotEvent.SLOT_RENDERED_EVENT,
			({ slot }) => {
				if (!slot.isEnabled()) {
					return;
				}

				logger(groupName, `Repeating: ${slotConfig.slotName}`);

				slotConfig.repeat.index += 1;

				const newSlotName = this.slotRepeater.repeatSlot(slot, slotConfig.repeat);

				if (!newSlotName) {
					return;
				}

				const updatedSlotConfig = {
					...slotConfig,
					...(slotConfig.repeat.updateCreator || {}),
					slotName: newSlotName,
				};

				try {
					this.createSlot(updatedSlotConfig);
				} catch (e) {
					logger(groupName, `There is not enough space for ${newSlotName}`);

					return;
				}

				logger(groupName, 'Injecting slot:', newSlotName);

				if (slotConfig.repeat.disablePushOnScroll !== true) {
					context.push('events.pushOnScroll.ids', newSlotName);
				}
			},
			slotConfig.slotName,
		);
	}

	private throwNoPlaceToInsertError(slotName: string): void {
		throw new Error(`No place to insert slot ${slotName}.`);
	}
}
